import { verify, hash, ArgonType } from 'argon2-browser';
import _ from 'lodash-es';
import Defaults from '~/scripts/constants/defaults';

/**
 * @param {import('angular').ITimeoutService} $timeout
 * @returns {any}
 */
const kformInputPasswordDirective = /* @ngInject */ $timeout => ({
	restrict: 'A',
	require: {
		ngModelCtrl: 'ngModel',
		formCtrl:    '^^?form',
	},
	scope: {
		password: '=kformInputPassword',
		validate: '=kformInputPasswordValidate',
	},
	/**
	 * @param {any} scope
	 * @param {any} element
	 * @param {any} attributes
	 * @param {{
	 *   ngModelCtrl: import('angular').INgModelController,
	 *   formCtrl: import('angular').IFormController,
	 * }} controllers
	 */
	link(scope, element, attributes, controllers) {
		const { ngModelCtrl, formCtrl } = controllers;
		let initialValue = scope.password?.name;
		/** @type {string | undefined} */
		let plainPassword = undefined;

		scope.$watch('validate', (/** @type {any} */ newValue, /** @type {any} */ oldValue) => {
			if (!_.isEqual(newValue, oldValue)) {
				formCtrl?.$setDirty();
			}
			ngModelCtrl.$validate();
		}, true);

		const checkPassword = (/** @type {string} */ value) => {
			const isHash = scope.password?.process?.value === 'K_NODE;HASH';
			if (isHash && value.startsWith('$argon2id')) {
				return true;
			}
			if (scope.validate?.pattern) {
				const reg = new RegExp(scope.validate.pattern);
				if (!reg.test(value)) {
					return false;
				}
			}

			return true;
		}

		ngModelCtrl.$asyncValidators.hashing = async (
			/** @type {string} */ modelValue,
			/** @type {string} */ viewValue,
		) => {
			const value = modelValue || viewValue;
			const isHash = scope.password?.process?.value === 'K_NODE;HASH';

			if (isHash && !element.is(':disabled') && value !== initialValue) {
				initialValue = value;
				if (value.startsWith('$argon2')) {
					ngModelCtrl.$setViewValue(value);
					ngModelCtrl.$setValidity('notHashing', true);

					return value;
				}

				plainPassword = value;

				if (!checkPassword(value)) {
					return false;
				}

				ngModelCtrl.$setValidity('notHashing', false);

				element.prop('disabled', true);
				scope.password.loading = true;

				try {
					await verify({
						pass:    value,
						encoded: initialValue,
					});
					ngModelCtrl.$setViewValue(initialValue);
					ngModelCtrl.$setValidity('notHashing', true);

					// keep the same hash
					scope.password.name = initialValue;
					element.prop('disabled', false);
					delete scope.password.loading;

					// sets the form in dirty state (because it doesn't do it!)
					$timeout(() => {
						formCtrl?.$setDirty();
						scope.$apply();
					});

					return true;
				}
				catch {
					const salt = new Uint32Array(16);
					const enc = new TextDecoder('utf8');
					crypto.getRandomValues(salt);

					const argonHash = await hash({
						pass:        value,
						salt:        enc.decode(salt),
						time:        Defaults.security.argon2.ITERATIONS_NB,
						mem:         Defaults.security.argon2.MEMORY_USED,
						parallelism: Defaults.security.argon2.PARALLELISM,
						type:        ArgonType.Argon2id,
					});

					if (argonHash) {
						ngModelCtrl.$setViewValue(argonHash.encoded);
						ngModelCtrl.$setValidity('notHashing', true);
						scope.password.name = argonHash.encoded;
						element.prop('disabled', false);
						delete scope.password.loading;

						// sets the form in dirty state (because it doesn't do it!)
						$timeout(() => {
							formCtrl?.$setDirty();
							scope.$apply();
						});
					}
				}
			}

			return value;
		};

		ngModelCtrl.$validators.pattern = (/** @type {string} */ modelValue, /** @type {string} */ viewValue) => {
			const value = plainPassword || modelValue || viewValue;
			return checkPassword(value);
		};

		ngModelCtrl.$parsers.push(value => {
			if (!value.startsWith('$argon2')) {
				plainPassword = value;
			}
			return value;
		});


		scope.$on('Form.submitted', () => {
			initialValue = scope.password?.name;
			element.val(initialValue);
		});
	},
});

export default kformInputPasswordDirective;
